import { useState, useEffect } from 'react';
import { useLocalStorage } from './use-hooks';
import { balance as fetchBalance } from '../api/account';
import {
  getUserLeague,
  getUserProfile,
  getUserWinProgress,
  persistUserProfile,
} from '../api/profile';
import { useContext } from 'react';
import { createContext } from 'react';
import { useMemo } from 'react';
import { loadPersistentState, persistState } from '../utils/persist';
import { useInterval } from './use-interval';
import { useLoggerContext } from './use-logger';
import { useTelegramContext } from './use-telegram';
import { isTelegram } from '../utils';

const User = createContext();
export function UserContext({ children }) {
  const userProps = useUser();
  const persistedUserProps = useMemo(() => userProps, [userProps]);
  return <User.Provider value={persistedUserProps}>{children}</User.Provider>;
}
export const useUserContext = () => useContext(User);

function useUser() {
  const logger = useLoggerContext();
  const [isLogged, setIsLogged] = useState(false);
  const [onLogout, setOnLogout] = useState(null);
  const [email, setEmail] = useLocalStorage('email', '');
  const [session, setSession] = useState(loadPersistentState('session'));
  const [lang, setLang] = useLocalStorage('lng', null);
  const [_nickname, setNickname] = useLocalStorage('nickname', '');
  const [premiumExpiresAt, setPremiumExpiresAt] = useState(null);
  const { user: tgUser } = useTelegramContext();
  const [league, setLeague] = useLocalStorage('league', 0);
  const [prevLeague, setPrevLeague] = useState(null);
  const [prevLeaguePlace, setPrevLeaguePlace] = useState(null);
  const [minBet, setMinBet] = useState(1);
  const [maxBet, setMaxBet] = useState(100000);
  const [maxBetAsBalanceShare, setMaxBetAsBalanceShare] = useState(1);

  const [isLoading, setIsLoading] = useState(false);
  const [isInited, setIsInited] = useState(false);
  const initBalance = 5;
  const [balance, setBalance] = useState(initBalance);
  const [winBalance, setWinBalance] = useState(0);

  async function fetchUserData(email) {
    setIsLoading(true);
    try {
      const resps = await Promise.all([
        fetchBalance(),
        getUserWinProgress(),
        getUserProfile(),
        getUserLeague(),
      ]);

      const [respBalance, respWinProgress, respProfile, respLeague] = resps;

      const errors = [
        respBalance.error,
        respWinProgress.error,
        respProfile.error,
        respLeague.error,
      ];

      /*
      //Logout
      if (errors.includes('jwt token expired')) {
        setIsLoading(false);
        resetUser('expired');
        return {};
      }

      if (errors.includes('jwt token not valid')) {
        setIsLoading(false);
        resetUser('invalid');
        return {};
      }

      if (respBalance.error)
        logger.event('error', { msg: 'respBalance', error: respBalance.error });
      if (respWinProgress.error)
        logger.event('error', {
          msg: 'respWinProgress',
          error: respWinProgress.error,
        });
      if (respProfile.error)
        logger.event('error', { msg: 'respProfile', error: respProfile.error });
      if (respLeague.error)
        logger.event('error', { msg: 'respLeague', error: respLeague.error });
 */

      if (
        respBalance.error ||
        respWinProgress.error ||
        respLeague.error ||
        respProfile.error
      ) {
        if (
          respBalance?.error?.indexOf('401') >= 0 ||
          respWinProgress?.error?.indexOf('401') >= 0 ||
          respLeague?.error?.indexOf('401') >= 0 ||
          respProfile?.error?.indexOf('401') >= 0
        ) {
          resetUser('expired');
        }
        setIsLoading(false);
        return {};
      }

      //Balance
      const newBalance = Number(
        respBalance.balances?.find((b) => b.code === 'main')?.amount // or === 'bonus'
      );
      setBalance(newBalance ? newBalance : 0);

      //Win progress
      const winProgress = Number(respWinProgress.winProgressDaily);
      setWinBalance(winProgress ? winProgress : 0); //setWinBalancePersisted(newWinBalance ? newWinBalance * 1 : 0);

      logger?.event('fetchUserData', {
        balance,
        newBalance,
        respBalance,
        winProgress,
        respWinProgress,
      });
      //League
      const leagueData = respLeague.league;
      setLeague(leagueData.league);
      setPrevLeague(leagueData.prev_league);
      setPrevLeaguePlace(leagueData.prev_place);

      const newMinBet = leagueData.min_bet ?? 1;
      const newMaxBet = leagueData.max_bet ?? 100;
      const newMaxBetAsBalanceShare = leagueData.max_bet_balance_share ?? 0.04; //% of balance
      setMinBet(newMinBet);
      setMaxBet(newMaxBet);
      setMaxBetAsBalanceShare(newMaxBetAsBalanceShare);

      //Profile
      const profile = respProfile.profile;
      if (profile) {
        const newNickName = profile.nickname
          ? profile.nickname
          : isTelegram() && tgUser?.first_name !== ''
          ? tgUser?.first_name
          : email?.length > 0
          ? email?.split('@').shift()
          : email;
        setNickname(newNickName);
        //setLeague(profile.league);
        if (profile?.lang) setLang(profile.lang);
        else persistUserProfile({ lang });

        persistState('enable_push_news', profile?.enable_push_news);
        persistState('enable_push_vip', profile?.enable_push_vip);
        persistState('enable_push_bonus', profile?.enable_push_bonus);

        const newPremiumExpiresAt = profile?.premium_expires_at
          ? new Date(profile?.premium_expires_at)?.getTime()
          : 0;
        setPremiumExpiresAt(newPremiumExpiresAt);
        setIsLoading(false);
        setIsInited(true);
        return {
          email,
          nickName: newNickName,
          league: leagueData.league,
          prevLeague: leagueData.prev_league,
          prevLeaguePlace: leagueData.prev_place,
          minBet: newMinBet,
          maxBet: newMaxBet,
          maxBetAsBalanceShare: newMaxBetAsBalanceShare,
        };
      }
      setIsLoading(false);
      return {
        email,
      };
    } catch (e) {
      setIsLoading(false);
      resetUser(`error: ${e.message}`);
      logger.event('error', { msg: 'fetchUserData', error: e.message });
      if (process.env.NODE_ENV !== 'production')
        alert(`Init user error: ${e.message}`);
      return {};
    }
  }

  //Init
  useEffect(() => {
    const session = loadPersistentState('session');
    //Init is moved to useInterval
    //if (session) initUser(email);
    //else resetUser('init');
  }, []);

  //Listen logout
  useInterval(() => {
    const newSession = loadPersistentState('session');
    if (!newSession?.token && session?.token) resetUser('listen');
    if (!isInited && !isLoading) {
      if (session) initUser(email);
      else resetUser('init');
    }
  }, 2000);

  //Call it after login
  async function initUser(email) {
    const newSession = loadPersistentState('session');
    setSession(newSession);
    setEmail(email);
    const result = await fetchUserData(email);
    if (result.email) setIsLogged(true);
    return result;
  }

  //Setter for logout callback
  const setOnUserLogout = (callback) => {
    setOnLogout(() => () => !!callback && callback());
  };

  //Call it after logout
  function resetUser(msg = 'default', newInitBalance) {
    if (email) logger?.event('logout', { email, msg });
    setBalance(newInitBalance ?? initBalance);
    setWinBalance(0);
    setEmail('');
    setNickname(null);
    setLang(null);
    setSession(null);
    persistState('session', null);
    setIsLogged(false);
    setIsLoading(false);
    setIsInited(true);
    !!onLogout && onLogout();
  }

  return {
    initUser,
    resetUser,
    user: {
      email,
      league,
      prevLeague,
      prevLeaguePlace,
      minBet,
      maxBet,
      maxBetAsBalanceShare,
      premiumExpiresAt,
    },
    balance,
    winBalance,
    setBalance,
    setWinBalance,
    setOnUserLogout,
    isLoading,
    isLogged,
  };
}
